import { ChangeEvent, VFC } from 'react';
import { Col, Container, Hidden, Row, useScreenClass } from 'react-grid-system';
import styled from 'styled-components';
import { useOrderContext } from '../../contexts/orderContext';
import { useSettings } from '../../contexts/settingsContext';
import { useTranslation } from '../../contexts/translationContext';
import { tOrder } from '../../contexts/zod';
import { EASYCOM_ROUTES } from '../../navigation';
import Spacing from '../atoms/Spacing';
import Typography from '../atoms/Typography';
import PrimaryButton from '../molecules/PrimaryButton';
import SearchInput from '../molecules/SearchInput';
import TableItem from '../molecules/TableItem';
import { useAppNavigate } from '../../hooks/useAppNavigate';

const StyledRow = styled(Row)`
  border-bottom: 1px solid #e0e0e0;
`;

const OrderBody: VFC<tOrder> = ({
  orderLines,
  orderDate,
  orderNumber,
  orderNumberAliases,
}) => {
  const navigate = useAppNavigate();
  const { search, setSearch } = useOrderContext();
  const { translate } = useTranslation();
  const screenClass = useScreenClass();
  const { showB2BCSVImport, productSearchEnabled } = useSettings();
  const table = [
    {
      title: <Typography translateKey="order_date" variant="body1-semi-bold" />,
      value: orderDate || 'no date supplied',
      key: 'order_date',
    },
    {
      title: (
        <Typography translateKey="no_of_products" variant="body1-semi-bold" />
      ),
      value: orderLines?.length || 0,
      key: 'no_of_products',
    },
  ];

  const goToReturnByFilePage = () => {
    navigate(EASYCOM_ROUTES.RETURNS_FILEUPLOAD);
  };

  const formatOrderAliases = (orderAliases: string[]) => orderAliases.join();
  const shouldShow = ['md', 'lg', 'xl', 'xxl', 'xxxl'].includes(screenClass);

  return (
    <Container>
      <StyledRow>
        <Col sm={12}>
          <Spacing spacing={4} />
          <Typography translateKey="order_no" variant="body2-extra-bold" />
          <Typography variant="h3">{orderNumber}</Typography>
          {orderNumberAliases && (
            <Typography variant="body3">
              {`${translate('order_aliases', null)} ${formatOrderAliases(
                orderNumberAliases,
              )}`}
            </Typography>
          )}
          <Spacing />
        </Col>
        {table.map(({ title, value, key }) => (
          <Col key={key} sm={6} md={4} lg={3}>
            <TableItem title={title} value={value} />
            <Hidden md lg xl xxl xxxl>
              <Spacing spacing={2} />
            </Hidden>
          </Col>
        ))}

        <Col xs={12}>
          <Spacing spacing={shouldShow ? 3 : 0} />
          <Row nogutter>
            <Col xs={12} md={6} lg={4}>
              <SearchInput
                type="text"
                value={search}
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  setSearch(e.target.value)
                }
                placeholder="placeholder_search"
                icon="search"
              />
              <Spacing spacing={2} />
              {showB2BCSVImport && (
                <PrimaryButton
                  translateKey="import_from_csv"
                  icon="arrow-right"
                  onClick={goToReturnByFilePage}
                />
              )}
            </Col>
          </Row>
          <Spacing spacing={4} />
        </Col>
      </StyledRow>
    </Container>
  );
};

export default OrderBody;
